
// Form5.jsx (no auto-save)
import { useState, useRef, useEffect, useCallback } from "react";
import styles from "./Form5.module.scss";
import saveIcon from "../../../assets/images/save-icon.svg";
import { Getcanleaddata, GetCourses, PostRegister } from "../../../apiServices";
import { decryptedAdminID, MyModal } from "../../../utility";
import ModalContainer from "../../../components/modal_popup";

const NOTICE_PERIOD_OPTIONS = ["None", "Immediate", "15 Days", "30 Days", "45 Days", "60 Days", "90 Days", "More than 90 Days"];

function CustomSelect({ options, placeholder, value, onChange, onBlur, isError, disabled }) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const wrapRef = useRef(null);
  const dropdownRef = useRef(null);
  const filtered = options.filter((o) => o.toLowerCase().includes(search.toLowerCase()));

  useEffect(() => {
    const handler = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) {
        setOpen(false);
        setSearch("");
        if (onBlur) onBlur();
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [onBlur]);

  useEffect(() => {
    if (open && dropdownRef.current && wrapRef.current) {
      const dropdown = dropdownRef.current;
      const rect = wrapRef.current.getBoundingClientRect();
      const spaceBelow = window.innerHeight - rect.bottom;
      if (spaceBelow < 250 && rect.top > 250) {
        dropdown.style.top = 'auto';
        dropdown.style.bottom = '100%';
        dropdown.style.marginTop = '0';
        dropdown.style.marginBottom = '4px';
      } else {
        dropdown.style.top = '100%';
        dropdown.style.bottom = 'auto';
        dropdown.style.marginTop = '4px';
        dropdown.style.marginBottom = '0';
      }
    }
  }, [open]);

  return (
    <div className={styles.customSelectWrapper} ref={wrapRef}>
      <div className={`${styles.customSelectTrigger} ${isError ? styles.err : ""} ${disabled ? styles.disabled : ""}`} onClick={() => !disabled && setOpen(p => !p)}>
        <span className={value ? styles.selectedVal : styles.placeholderVal}>{value || placeholder}</span>
        <span className={styles.arrow}>{open ? "▴" : "▾"}</span>
      </div>
      {open && (
        <div className={styles.customDropdown} ref={dropdownRef}>
          <div className={styles.searchWrap}>
            <input autoFocus className={styles.searchInput} placeholder="Search..." value={search} onChange={(e) => setSearch(e.target.value)} onClick={(e) => e.stopPropagation()} />
          </div>
          <ul className={styles.optionList}>
            {filtered.length > 0 ? (
              filtered.map((opt) => (
                <li key={opt} className={`${styles.optionItem} ${value === opt ? styles.active : ""}`}
                  onClick={() => { onChange(opt); setOpen(false); setSearch(""); if (onBlur) onBlur(); }}>
                  {opt}
                </li>
              ))
            ) : <li className={styles.noOption}>No options found</li>}
          </ul>
        </div>
      )}
    </div>
  );
}

function MultiSelect({ options, placeholder, selectedItems, onSelect, onRemove, onBlur, isError, disabled, loading }) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const wrapRef = useRef(null);
  const dropdownRef = useRef(null);

  const availableOptions = options.filter(
    (opt) => !selectedItems.includes(opt) && opt.toLowerCase().includes(search.toLowerCase())
  );

  useEffect(() => {
    const handler = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) {
        setOpen(false);
        setSearch("");
        if (onBlur) onBlur();
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [onBlur]);

  useEffect(() => {
    if (open && dropdownRef.current && wrapRef.current) {
      const dropdown = dropdownRef.current;
      const rect = wrapRef.current.getBoundingClientRect();
      const spaceBelow = window.innerHeight - rect.bottom;
      if (spaceBelow < 250 && rect.top > 250) {
        dropdown.style.top = 'auto';
        dropdown.style.bottom = '100%';
        dropdown.style.marginTop = '0';
        dropdown.style.marginBottom = '4px';
      } else {
        dropdown.style.top = '100%';
        dropdown.style.bottom = 'auto';
        dropdown.style.marginTop = '4px';
        dropdown.style.marginBottom = '0';
      }
    }
  }, [open]);

  return (
    <div className={styles.multiSelectWrapper} ref={wrapRef}>
      <div className={`${styles.selectedItems} ${isError ? styles.inputError : ""}`}>
        {selectedItems.map((item, index) => (
          <span key={index} className={styles.selectedTag}>
            {item}
            <button type="button" className={styles.removeTag} onClick={() => onRemove(item)} disabled={disabled}>×</button>
          </span>
        ))}
        <div className={styles.inputWrapper}>
          <input type="text" placeholder={selectedItems.length === 0 ? placeholder : ""} value={search} onChange={(e) => setSearch(e.target.value)} onFocus={() => setOpen(true)} disabled={disabled} className={styles.skillInput} />
          <button type="button" className={styles.dropdownBtn} onClick={() => !disabled && setOpen(p => !p)} disabled={disabled}>
            {open ? "▴" : "▾"}
          </button>
        </div>
      </div>
      {open && (
        <div className={styles.dropdown} ref={dropdownRef}>
          <ul className={styles.optionList}>
            {loading ? (
              <li className={styles.noOption}>Loading...</li>
            ) : availableOptions.length > 0 ? (
              availableOptions.map((item, i) => (
                <li key={i} className={styles.optionItem} onClick={() => { onSelect(item); setSearch(""); setOpen(false); if (onBlur) onBlur(); }}>
                  {item}
                </li>
              ))
            ) : <li className={styles.noOption}>{search ? "No matching options found" : "No options available"}</li>}
          </ul>
        </div>
      )}
    </div>
  );
}

function TextArea({ name, placeholder, value, onChange, onBlur, isError, disabled, rows = 4 }) {
  return (
    <div className={`${styles.textareaWrap}${isError ? " " + styles.inputError : ""}${disabled ? " " + styles.disabled : ""}`}>
      <textarea name={name} placeholder={placeholder} value={value || ""} onChange={onChange} onBlur={onBlur} disabled={disabled} rows={rows} />
    </div>
  );
}

function RadioGroup({ label, name, value, onChange, onBlur, options, isError, required = true }) {
  return (
    <div className={styles.radioGroup}>
      <label className={styles.radioLabel}>
        {label} {required && <span className={styles.required}>*</span>}
      </label>
      <div className={styles.radioOptions}>
        {options.map((option) => (
          <label key={option.value} className={styles.radioOption}>
            <input type="radio" name={name} value={option.value} checked={value === option.value} onChange={onChange} onBlur={onBlur} />
            <span>{option.label}</span>
          </label>
        ))}
      </div>
      {isError && <span className={styles.errorMsg}>This field is required</span>}
    </div>
  );
}

export default function Form5({ onClose, id, recordType, form4Data, onSubmit, onBack, registerValidation, onValidationChange,onSuccess,isEditMode = false    }) {
  const [formData, setFormData] = useState({
    noticePeriod: "", willingToWorkRotationalShift: "", accommodationRequired: "",
    notes: "", languages: [], certifications: [] ,mobileNumber:"",candidateName:""
  });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [touched, setTouched] = useState({});
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [certificationOptions, setCertificationOptions] = useState([]);
  const [loadingCertifications, setLoadingCertifications] = useState(false);

  const languageOptions = [
    "English", "Hindi", "Tamil", "Telugu", "Kannada", "Malayalam",
    "Marathi", "Gujarati", "Bengali", "Punjabi", "Urdu", "Odia",
    "Assamese", "French", "German", "Spanish", "Chinese", "Japanese",
    "Korean", "Russian", "Arabic", "Portuguese", "Italian", "Dutch", "Other"
  ];

  // --- FIX: stable refs ---
  const validateRef = useRef(null);
  const formDataRef = useRef(formData);
  formDataRef.current = formData;

  const prevIsValidRef = useRef(true);

  useEffect(() => { fetchCertifications(); }, []);
  useEffect(() => { if (id) fetchAdditionalData(id); }, [id, recordType]);

  const fetchCertifications = async () => {
    setLoadingCertifications(true);
    try {
      const response = await GetCourses();
      if (response?.statuscode === 200 && response.results) {
        setCertificationOptions(response.results.map(item => item.courses));
      }
    } catch (error) { console.error("Error fetching certifications:", error); }
    finally { setLoadingCertifications(false); }
  };

  const fetchAdditionalData = async (recordId) => {
    setLoading(true);
    try {
      const response = await Getcanleaddata(recordId, recordType);
      if (response?.code === 200) {
        const candidate = response.data;
        const languagesList = candidate.known_languages ? candidate.known_languages.split(",").map(l => l.trim()) : [];
        const certificationsList = candidate.certification_courses ? candidate.certification_courses.split(",").map(c => c.trim()) : [];

        setFormData({
          candidateName: candidate.name || "",
          mobileNumber: candidate.mobile_number || "",
          noticePeriod: candidate.notice_period || "",
          willingToWorkRotationalShift: candidate.is_ready_for_shifts === 1 ? "yes" : candidate.is_ready_for_shifts === 0 ? "no" : "",
          accommodationRequired: candidate.is_need_accommodation === 1 ? "yes" : candidate.is_need_accommodation === 0 ? "no" : "",
          notes: candidate.notes || "",
          languages: languagesList,
          certifications: certificationsList
        });
        setTouched({
          accommodationRequired: !!candidate.is_need_accommodation,
          willingToWorkRotationalShift: !!candidate.is_ready_for_shifts,
          noticePeriod: !!candidate.notice_period,
          languages: languagesList.length > 0,
          certifications: certificationsList.length > 0,
          notes: !!candidate.notes
        });
      }
    } catch (error) { console.error("Error fetching additional data:", error); }
    finally { setLoading(false); }
  };

  // validate reads from ref - no deps
  const validate = useCallback((silent = false) => {
    const fd = formDataRef.current;
    const isShiftValid = !!fd.willingToWorkRotationalShift;
    const isAccommodationValid = !!fd.accommodationRequired;
    const isValid = isShiftValid && isAccommodationValid;

    if (!silent) {
      const errs = {};
      if (!isShiftValid) errs.willingToWorkRotationalShift = "This field is required";
      if (!isAccommodationValid) errs.accommodationRequired = "This field is required";
      setErrors(errs);
    }
    return isValid;
  }, []);

  validateRef.current = validate;

  // Register validation ONCE
  useEffect(() => {
    if (registerValidation) {
      registerValidation((silent = false) => validateRef.current(silent));
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const buildApiData = () => {
    const fd = formDataRef.current;
    return {
      admin_id: decryptedAdminID(),
      is_need_accommodation: fd.accommodationRequired === "yes" ? "yes" : "no",
      is_ready_for_shifts: fd.willingToWorkRotationalShift === "yes" ? "yes" : "no",
      notice_period: fd.noticePeriod,
      known_languages: fd.languages.join(","),
      certification_courses: fd.certifications.join(","),
      notes: fd.notes,
    };
  };

  useEffect(() => {
    if (onValidationChange) {
      const isValid = !!formData.accommodationRequired && !!formData.willingToWorkRotationalShift;
      if (prevIsValidRef.current !== isValid) {
        prevIsValidRef.current = isValid;
        onValidationChange();
      }
    }
  }, [formData.accommodationRequired, formData.willingToWorkRotationalShift, onValidationChange]);

  const handleSelect = useCallback((field) => (val) => {
    setTouched(prev => ({ ...prev, [field]: true }));
    setFormData(prev => ({ ...prev, [field]: val }));
    if (val) setErrors(prev => ({ ...prev, [field]: "" }));
  }, []);

  const handleRadioChange = useCallback((field) => (e) => {
    setTouched(prev => ({ ...prev, [field]: true }));
    setFormData(prev => ({ ...prev, [field]: e.target.value }));
    if (e.target.value) setErrors(prev => ({ ...prev, [field]: "" }));
  }, []);

  const handleTextChange = useCallback((e) => {
    const { name, value } = e.target;
    setTouched(prev => ({ ...prev, [name]: true }));
    setFormData(prev => ({ ...prev, [name]: value }));
  }, []);

  const handleBlur = useCallback((field) => () => {
    setTouched(prev => ({ ...prev, [field]: true }));
  }, []);

  const handleLanguageSelect = useCallback((language) => {
    setTouched(prev => ({ ...prev, languages: true }));
    setFormData(prev => ({ ...prev, languages: [...prev.languages, language] }));
  }, []);

  const handleLanguageRemove = useCallback((lang) => {
    setTouched(prev => ({ ...prev, languages: true }));
    setFormData(prev => ({ ...prev, languages: prev.languages.filter(l => l !== lang) }));
  }, []);

  const handleCertificationSelect = useCallback((cert) => {
    setTouched(prev => ({ ...prev, certifications: true }));
    setFormData(prev => ({ ...prev, certifications: [...prev.certifications, cert] }));
  }, []);

  const handleCertificationRemove = useCallback((cert) => {
    setTouched(prev => ({ ...prev, certifications: true }));
    setFormData(prev => ({ ...prev, certifications: prev.certifications.filter(c => c !== cert) }));
  }, []);

  // const handleSaveClick = useCallback(() => {
  //   const isForm5Valid = validateRef.current(false);
  //   if (isForm5Valid) {
  //     if (onSubmit) {
  //       onSubmit({
  //         triggerConfirm: true,
  //         confirmCallback: () => setShowConfirmation(true),
  //       });
  //     }
  //   } else {
  //     setTimeout(() => {
  //       const el = document.querySelector('[data-field="accommodationRequired"], [data-field="willingToWorkRotationalShift"]');
  //       if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
  //     }, 50);
  //   }
  // }, [onSubmit]);




 const submitToAPI = useCallback(async () => {
  setLoading(true);
  setShowConfirmation(false);
  try {
    const apiData = buildApiData();
    const response = await PostRegister(id, 5, apiData, recordType);
    if (response?.code === 200) {
      // Call GET API after submit
      await Getcanleaddata(id, recordType);
      
      if (onSuccess && typeof onSuccess === 'function') {
        onSuccess();
      }
      
      if (onSubmit) {
        if (isEditMode) {
          // For edit mode, just close without showing success message
          onSubmit({ triggerConfirm: false, apiData, isEditMode: true });
        } else {
          onSubmit({ triggerConfirm: false, apiData, isEditMode: false });
        }
      }
    } else {
      setErrors({ submit: response?.message || "Failed to save additional details" });
    }
  } catch (error) {
    console.error("Error saving form5:", error);
    setErrors({ submit: "Network error. Please try again." });
  } finally { 
    setLoading(false);
  }
}, [id, recordType, onSubmit, onSuccess, isEditMode]);
const handleSaveClick = useCallback(() => {
  const isForm5Valid = validateRef.current(false);
  if (isForm5Valid) {
    if (onSubmit) {
      if (!isEditMode) {
        // Show confirmation modal only for new registration
        onSubmit({
          triggerConfirm: true,
          confirmCallback: () => setShowConfirmation(true),
        });
      } else {
        // For edit mode, submit directly without confirmation
        submitToAPI();
      }
    }
  } else {
    setTimeout(() => {
      const el = document.querySelector('[data-field="accommodationRequired"], [data-field="willingToWorkRotationalShift"]');
      if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
    }, 50);
  }
}, [onSubmit, isEditMode, submitToAPI]);

  if (loading) {
    return (
      <div className={styles.formContainer}>
        <div className={styles.formContent}><div className={styles.formTitle}>Submitting Candidate Details...</div></div>
      </div>
    );
  }

  return (
    <>
      <div className={styles.formContainer}>
        <div className={styles.formContent}>
          <div className={styles.formTitle}>Additional Details</div>
          {errors.submit && <div className={styles.errorSummary}>{errors.submit}</div>}

          <div className={styles.formRow}>
            <div className={styles.formGroup} data-field="accommodationRequired">
              <RadioGroup
                label="Accommodation Required?"
                name="accommodationRequired"
                value={formData.accommodationRequired}
                onChange={handleRadioChange("accommodationRequired")}
                onBlur={handleBlur("accommodationRequired")}
                options={[{ value: "yes", label: "Yes" }, { value: "no", label: "No" }]}
                isError={!!errors.accommodationRequired}
              />
            </div>
            <div className={styles.formGroup} data-field="willingToWorkRotationalShift">
              <RadioGroup
                label="Willing to work in rotational shift"
                name="willingToWorkRotationalShift"
                value={formData.willingToWorkRotationalShift}
                onChange={handleRadioChange("willingToWorkRotationalShift")}
                onBlur={handleBlur("willingToWorkRotationalShift")}
                options={[{ value: "yes", label: "Yes" }, { value: "no", label: "No" }]}
                isError={!!errors.willingToWorkRotationalShift}
              />
            </div>
          </div>

          <div className={styles.formRow}>
            <div className={styles.formGroup} data-field="noticePeriod">
              <label>Notice Period (Optional)</label>
              <CustomSelect options={NOTICE_PERIOD_OPTIONS} placeholder="Select notice period" value={formData.noticePeriod} onChange={handleSelect("noticePeriod")} onBlur={handleBlur("noticePeriod")} isError={!!errors.noticePeriod} />
            </div>
            <div className={styles.formGroup} data-field="languages">
              <label>Languages Known (Optional)</label>
              <MultiSelect options={languageOptions} placeholder="Search and select languages..." selectedItems={formData.languages} onSelect={handleLanguageSelect} onRemove={handleLanguageRemove} onBlur={handleBlur("languages")} isError={!!errors.languages} />
            </div>
          </div>

          <div className={styles.formGroup} data-field="certifications">
            <label>Certifications (Optional)</label>
            <MultiSelect
              options={certificationOptions}
              placeholder={loadingCertifications ? "Loading certifications..." : "Search and select certifications..."}
              selectedItems={formData.certifications}
              onSelect={handleCertificationSelect}
              onRemove={handleCertificationRemove}
              onBlur={handleBlur("certifications")}
              isError={!!errors.certifications}
              loading={loadingCertifications}
            />
          </div>

          <div className={styles.formGroup} data-field="notes">
            <label>Notes (Optional)</label>
            <TextArea name="notes" placeholder="Enter any additional notes or comments..." value={formData.notes} onChange={handleTextChange} onBlur={handleBlur("notes")} isError={!!errors.notes} rows={4} />
          </div>
        </div>

        <div className={styles.buttonWrapper}>
          <div className={styles.buttonGroup}>
            <button className={styles.btnCancel} onClick={onBack}>Back</button>
            <button className={styles.btnSave} onClick={handleSaveClick}>
              <img src={saveIcon} alt="" width="16" height="16" />
              {/* Submit */}
                 {isEditMode ? "Save & Close" : "Submit"}
            </button>
          </div>
        </div>
      </div>

      {showConfirmation && (
        <MyModal>
          <ModalContainer zIndex={1500} childComponent={
            <div style={{ padding: "20px", textAlign: "center" }}>
              <h3 style={{ marginBottom: "15px" }}>Confirm Submission</h3>
              <p style={{ marginBottom: "25px" }}>Are you sure you want to move this candidate <b>{formData.candidateName + "(" + formData.mobileNumber + ")"}</b> ?</p>
              <div style={{ display: "flex", gap: "10px", justifyContent: "center" }}>
                <button onClick={() => setShowConfirmation(false)} style={{ padding: "8px 20px", backgroundColor: "#bf2121", color: "white", border: "none", borderRadius: "4px", cursor: "pointer" }}>No</button>
                <button onClick={submitToAPI} style={{ padding: "8px 20px", backgroundColor: "#169c50", color: "white", border: "none", borderRadius: "4px", cursor: "pointer" }}>Yes</button>
              </div>
            </div>
          } />
        </MyModal>
      )}
    </>
  );
}